import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { TextInput } from '@cbhq/cds-web/controls/TextInput';
import { Modal } from '@cbhq/cds-web/overlays/modal/Modal';
import { ModalBody } from '@cbhq/cds-web/overlays/modal/ModalBody';
import { ModalFooter } from '@cbhq/cds-web/overlays/modal/ModalFooter';
import { ModalHeader } from '@cbhq/cds-web/overlays/modal/ModalHeader';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import { defineMessages, useIntl } from '@cbhq/i18n';

import { UnsavedChangesAlert } from './UnsavedChangesAlert';

export type SettingsFieldType = 'org_name' | 'legal_entity_name';

export type EditFieldModalProps = {
  visible: boolean;
  title: string;
  fieldType: SettingsFieldType;
  initialValue: string;
  onSave: (value: string) => void | Promise<void>;
  onClose: () => void;
  placeholder?: string;
  isSaving?: boolean;
};

const messages = defineMessages({
  save: {
    id: 'Settings.editFieldModal.save',
    defaultMessage: 'Save',
    description: 'Button label to save an edited Settings field',
  },
  cancel: {
    id: 'Settings.editFieldModal.cancel',
    defaultMessage: 'Cancel',
    description: 'Button label to cancel editing a Settings field',
  },
  close: {
    id: 'Settings.editFieldModal.close',
    defaultMessage: 'Close',
    description: 'Accessibility label to close the Settings edit field modal',
  },
});

export function EditFieldModal({
  visible,
  title,
  fieldType,
  initialValue,
  onSave,
  onClose,
  placeholder,
  isSaving = false,
}: EditFieldModalProps) {
  const { formatMessage } = useIntl();
  const [value, setValue] = useState(initialValue);
  const [alertVisible, setAlertVisible] = useState(false);
  const previousVisible = useRef(false);

  useEffect(() => {
    if (visible && !previousVisible.current) {
      setValue(initialValue);
      setAlertVisible(false);
    }

    previousVisible.current = visible;
  }, [initialValue, visible]);

  const trimmedValue = useMemo(() => value.trim(), [value]);
  const trimmedInitialValue = useMemo(() => initialValue.trim(), [initialValue]);
  const hasChanges = useMemo(
    () => trimmedValue !== trimmedInitialValue,
    [trimmedInitialValue, trimmedValue],
  );
  const isSaveDisabled = useMemo(
    () => isSaving || trimmedValue === '' || !hasChanges,
    [hasChanges, isSaving, trimmedValue],
  );

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  }, []);

  const handleSave = useCallback(() => {
    if (isSaveDisabled) {
      return;
    }

    logEvent('settings_field_edited', {
      componentType: ComponentType.button,
      action: ActionType.click,
      field_type: fieldType,
    });
    onSave(trimmedValue);
  }, [fieldType, isSaveDisabled, onSave, trimmedValue]);

  const handleClose = useCallback(() => {
    if (isSaving) {
      return;
    }

    if (hasChanges) {
      setAlertVisible(true);
      return;
    }

    onClose();
  }, [hasChanges, isSaving, onClose]);

  const handleKeepEditing = useCallback(() => {
    setAlertVisible(false);
  }, []);

  const handleDiscard = useCallback(() => {
    setAlertVisible(false);
    onClose();
  }, [onClose]);

  return (
    <>
      <Modal visible={visible} onRequestClose={handleClose} width={448} hideDividers>
        <ModalHeader title={title} closeAccessibilityLabel={formatMessage(messages.close)} />
        <ModalBody>
          <TextInput
            accessibilityLabel={title}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            disabled={isSaving}
            autoFocus
          />
        </ModalBody>
        <ModalFooter
          primaryAction={
            <Button
              onClick={handleSave}
              variant="primary"
              disabled={isSaveDisabled}
              loading={isSaving}
              compact
            >
              {formatMessage(messages.save)}
            </Button>
          }
          secondaryAction={
            <Button onClick={handleClose} variant="secondary" disabled={isSaving} compact>
              {formatMessage(messages.cancel)}
            </Button>
          }
        />
      </Modal>
      <UnsavedChangesAlert
        visible={alertVisible}
        onKeepEditing={handleKeepEditing}
        onDiscard={handleDiscard}
      />
    </>
  );
}
